import React from 'react';
import { Share2, AlertTriangle, Loader2, Check, ExternalLink, Trash2 } from 'lucide-react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../ui/hover-card';
import { getPlatformIcon, getPlatformColor, getPlatformName, buildPlatformUrl } from '../../utils/platformIcons';
import { cn } from '../../lib/utils';

interface PlatformStatus {
  platform: string;
  status?: string;
  externalPostId?: string;
  externalPostUrl?: string;
  result?: string;
  handle?: string;
  accountName?: string;
  scheduledAt?: string;
}

interface PublishStatusBadgeProps {
  platforms: PlatformStatus[];
  className?: string;
}

export function PublishStatusBadge({ platforms, className }: PublishStatusBadgeProps) {
  const totalCount = platforms.length;
  const failedPlatforms = platforms.filter(p => (p.status || 'PENDING') === 'FAILED');
  const hasAnyFailed = failedPlatforms.length > 0;
  const publishingPlatforms = platforms.filter(p => {
    const status = p.status || 'PENDING';
    if (status === 'PROCESSING') return true;
    if (status === 'PENDING' && p.scheduledAt) {
      const scheduled = new Date(p.scheduledAt).getTime();
      return scheduled <= Date.now() && scheduled > Date.now() - 5 * 60 * 1000;
    }
    return false;
  });
  const hasAnyPublishing = publishingPlatforms.length > 0;
  const completedCount = platforms.filter(p => (p.status || 'PENDING') === 'COMPLETED').length;

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] font-black transition-all hover:scale-105",
            hasAnyFailed
              ? "bg-red-500/10 border-red-500/30 text-red-400"
              : hasAnyPublishing
              ? "bg-primary/10 border-primary/30 text-primary animate-pulse"
              : "bg-muted/60 border-border/50 text-muted-foreground hover:text-foreground",
            className
          )}
        >
          <Share2 className="h-3 w-3" />
          <span>{completedCount}/{totalCount}</span>
          {hasAnyFailed && <AlertTriangle className="h-3 w-3 text-red-500" />}
          {hasAnyPublishing && !hasAnyFailed && <Loader2 className="h-3 w-3 animate-spin" />}
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="w-auto min-w-[240px] max-w-[320px] p-2" align="end" sideOffset={6}>
        <div className="space-y-0.5">
          {platforms.map((p, i) => {
            const builtUrl = buildPlatformUrl(p.platform, p.externalPostId, p.handle);
            const url = builtUrl || p.externalPostUrl || '';
            const status = p.status || 'PENDING';
            const isPublishing = status === 'PROCESSING' || (
              status === 'PENDING' &&
              p.scheduledAt &&
              new Date(p.scheduledAt).getTime() <= Date.now() &&
              new Date(p.scheduledAt).getTime() > Date.now() - 5 * 60 * 1000
            );

            return (
              <div
                key={i}
                className={cn(
                  "flex items-center gap-2.5 px-2 py-1.5 rounded-md text-xs transition-colors hover:bg-muted/80 cursor-default",
                  status === 'FAILED' && "bg-red-500/10 hover:bg-red-500/15",
                  status === 'DELETED' && "opacity-40"
                )}
              >
                {/* Platform icon (avatar) */}
                <div
                  className="shrink-0 h-7 w-7 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: getPlatformColor(p.platform) + '20', color: getPlatformColor(p.platform) }}
                >
                  {getPlatformIcon(p.platform, "h-3.5 w-3.5")}
                </div>

                {/* Platform name + account name */}
                <div className="flex-1 min-w-0">
                  <span className={cn(
                    "font-semibold block truncate text-foreground text-[11px]",
                    status === 'DELETED' && "line-through"
                  )}>
                    {getPlatformName(p.platform)}
                  </span>
                  {p.accountName && (
                    <span className="text-[10px] text-muted-foreground block truncate">{p.accountName}</span>
                  )}
                  {status === 'FAILED' && p.result && (
                    <span className="text-[10px] text-red-400 block truncate">{p.result}</span>
                  )}
                </div>

                {/* Status / external link */}
                <div className="shrink-0">
                  {isPublishing ? (
                    <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
                  ) : status === 'COMPLETED' ? (
                    url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-500 hover:text-green-400 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <Check className="h-3.5 w-3.5 text-green-500" />
                    )
                  ) : status === 'FAILED' ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                  ) : status === 'DELETED' ? (
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <div className="h-3.5 w-3.5" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
